import { Alert, Descriptions, Table, Tag } from 'antd';
import type { FanOutProjection } from '@/api/types';

type Edge = FanOutProjection['edges'][number];

const EDGE_COLUMNS = [
  { title: 'If the list holds', dataIndex: 'scenario', key: 'scenario' },
  {
    title: 'Result',
    key: 'status',
    render: (_: unknown, e: Edge) => (
      <Tag bordered={false} color={e.status === 'COMPLETED' ? 'success' : e.status === 'EMPTY' ? 'default' : 'warning'}>
        {e.status}
        {e.guardrail ? ` (${e.guardrail})` : ''}
        {e.certain ? '' : ', likely'}
      </Tag>
    ),
  },
  { title: 'Children', dataIndex: 'children', key: 'children', width: 90 },
  { title: 'Units', dataIndex: 'units', key: 'units', width: 80 },
  { title: 'Goes to', key: 'routesTo', render: (_: unknown, e: Edge) => e.routesTo ?? 'unrouted' },
];

/** What each fan-out in a flow can do, shown before publish (OL-25). */
export function FanOutReport({ fanOuts }: { fanOuts?: FanOutProjection[] }) {
  if (!fanOuts || fanOuts.length === 0) return null;
  return (
    <div data-testid="fan-out-report" style={{ marginBottom: 12 }}>
      {fanOuts.map((f) => (
        <div key={f.nodeId} style={{ marginBottom: 12 }}>
          <h4 style={{ margin: '8px 0', fontSize: 13 }}>Fan-out: {f.nodeId}</h4>
          {!f.templateResolved && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message={`Template ${f.template} does not compile, so the figures below leave out its cost.`}
            />
          )}
          {f.templateResolved && !f.budgetCoversMaxFanOut && (
            <Alert
              type="warning"
              showIcon
              data-testid="fan-out-budget-warning"
              style={{ marginBottom: 8 }}
              message={`The budget of ${f.budget} units covers about ${f.childrenInBudget} of ${f.maxFanOut} children at worst.`}
              description="The run stops through its limit route if the budget runs out. Budget is checked as children finish, so running children can overshoot it."
            />
          )}
          <Descriptions size="small" column={2} style={{ marginBottom: 8 }}>
            <Descriptions.Item label="Most children">{f.maxFanOut}</Descriptions.Item>
            <Descriptions.Item label="Units per child">{f.unitsPerChild}</Descriptions.Item>
            <Descriptions.Item label="Worst-case units">{f.worstCaseUnits}</Descriptions.Item>
            <Descriptions.Item label="Worst-case cost">{f.worstCaseCost.toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="Units spent before it">{f.unitsBefore}</Descriptions.Item>
            <Descriptions.Item label="Budget">{f.budget}</Descriptions.Item>
          </Descriptions>
          {/* `children` here is a count, not nested rows, so tree mode reads another field. */}
          <Table
            size="small"
            rowKey="scenario"
            pagination={false}
            columns={EDGE_COLUMNS}
            dataSource={f.edges}
            childrenColumnName="nestedEdges"
          />
        </div>
      ))}
    </div>
  );
}
